import { DcRuntimeApi } from '@api/bff/openapi/endpoints/dc-runtime-api';
import { WorkOrdersApi as BffWorkOrdersApi } from '@api/bff/openapi';
import { bffApiClient } from '@api/bff/client';
import { WorkOrdersApi, OrderDetailStatus } from '@api/enterprise/openapi';
import type { CreateWorkOrderDocumentCommand } from '@api/enterprise/openapi/models';
import { enterpriseApiClient } from '@api/enterprise/client';
import {
  registerHandler,
  registerRollbackHandler
} from '@services/offlineSyncService';
import { photosDb } from '@features/calibrationPhotos/db';
import { db } from '@db/database';
import type { SyncQueueItem } from '@db/database';
import { queryClient } from '../queryClient';
import { workOrderKeys } from '@api/enterprise/workorder/constants';
import { DC_RUNTIME_QUERY_KEY } from '@api/bff/calibrationRuntime/constants';
import { EVENT_DOCUMENTS_QUERY_KEY } from '@api/bff/eventDocuments/constants';
import type {
  CompleteCalibrationRequestDto,
  EventDocumentViewModel
} from '@api/bff/openapi/models';

const dcRuntimeApi = new DcRuntimeApi(undefined, undefined, bffApiClient);
const workOrdersApi = new WorkOrdersApi(
  undefined,
  undefined,
  enterpriseApiClient
);
const bffWorkOrdersApi = new BffWorkOrdersApi(
  undefined,
  undefined,
  bffApiClient
);

async function refreshCachedCharges(
  jobNumber: string,
  userId: string
): Promise<void> {
  try {
    const response = await workOrdersApi.apiWorkOrdersJobNumberChargesGet({
      jobNumber
    });

    await db.cachedCharges.where('jobNumber').equals(jobNumber).delete();

    if (response.data && response.data.length > 0) {
      const now = new Date().toISOString();
      for (const charge of response.data) {
        await db.cachedCharges.add({
          ...charge,
          jobNumber: String(jobNumber),
          cachedAt: now,
          _userId: userId
        });
      }
    }
  } catch {
    // Best effort — online React Query cache will still be fresh
  }
}

export function registerSyncHandlers(): void {
  registerHandler('acknowledgment', async (item: SyncQueueItem) => {
    const { calibrationRuntimeId, updateDcRuntimeDto } = item.payload as {
      calibrationRuntimeId: string;
      updateDcRuntimeDto: {
        gagesGlobalId: string;
        moduleType: string;
        woDetailGlobalId: string | null;
        lastModifiedBy: string;
        jsonData: string;
      };
    };

    await dcRuntimeApi.apiDcRuntimeIdPut({
      id: calibrationRuntimeId,
      updateDcRuntimeDto
    });
  });

  registerHandler('dc_runtime_create', async (item: SyncQueueItem) => {
    const { createDcRuntimeDto, tempId } = item.payload as {
      createDcRuntimeDto: {
        gagesGlobalId: string;
        moduleType: string;
        woDetailGlobalId?: string | null;
        lastModifiedBy: string;
        eventNum?: number | null;
        jsonData?: string | null;
      };
      tempId: string;
    };

    const woDetailGlobalIdValue = createDcRuntimeDto.woDetailGlobalId ?? '';

    let preservedJsonData: string | null = null;
    if (woDetailGlobalIdValue) {
      const existingCached = await db.cachedCalibrationRuntimes
        .where('woDetailGlobalId')
        .equals(woDetailGlobalIdValue)
        .first();
      if (existingCached?.jsonData) {
        preservedJsonData = existingCached.jsonData;
      }
    }

    const response = await dcRuntimeApi.apiDcRuntimePost({
      createDcRuntimeDto
    });
    const serverId = response.data.id;

    if (woDetailGlobalIdValue) {
      await db.cachedCalibrationRuntimes
        .where('woDetailGlobalId')
        .equals(woDetailGlobalIdValue)
        .delete();

      await db.cachedCalibrationRuntimes.add({
        ...response.data,
        jsonData: preservedJsonData ?? response.data.jsonData,
        cachedAt: new Date().toISOString(),
        _userId: item._userId
      });
    }

    if (serverId) {
      const pendingItems = await db.syncQueue
        .where('_userId')
        .equals(item._userId)
        .and(
          i =>
            (i.entityType === 'calibration_save' ||
              i.entityType === 'calibration_complete') &&
            i.status === 'pending' &&
            (i.payload as Record<string, unknown>).calibrationRuntimeId ===
              tempId
        )
        .toArray();

      for (const pending of pendingItems) {
        await db.syncQueue.update(pending.id!, {
          entityKey: pending.entityKey.replace(tempId, serverId),
          payload: {
            ...pending.payload,
            calibrationRuntimeId: serverId
          },
          updatedAt: new Date().toISOString()
        });
      }
    }

    const cachedJsonData = preservedJsonData ?? response.data.jsonData;
    if (woDetailGlobalIdValue) {
      queryClient.setQueryData(
        [DC_RUNTIME_QUERY_KEY, 'woDetailGlobalId', woDetailGlobalIdValue],
        {
          ...response.data,
          jsonData: cachedJsonData
        }
      );
    }
    if (serverId) {
      queryClient.setQueryData([DC_RUNTIME_QUERY_KEY, 'id', serverId], {
        ...response.data,
        jsonData: cachedJsonData
      });
    }
  });

  registerHandler('calibration_save', async (item: SyncQueueItem) => {
    let { calibrationRuntimeId, updateDcRuntimeDto } = item.payload as {
      calibrationRuntimeId: string;
      updateDcRuntimeDto: {
        gagesGlobalId: string;
        moduleType: string;
        woDetailGlobalId: string | null;
        lastModifiedBy: string;
        jsonData: string;
        eventNum?: number;
      };
    };

    if (calibrationRuntimeId.startsWith('temp-')) {
      const woDetailGlobalId = updateDcRuntimeDto.woDetailGlobalId;
      if (woDetailGlobalId) {
        const cached = await db.cachedCalibrationRuntimes
          .where('woDetailGlobalId')
          .equals(woDetailGlobalId)
          .first();

        if (cached?.id && !cached.id.startsWith('temp-')) {
          calibrationRuntimeId = cached.id;
        } else {
          throw new Error(
            'DC runtime create has not synced yet — will retry later'
          );
        }
      }
    }

    // Look up cached runtime by id, falling back to woDetailGlobalId
    let cached = await db.cachedCalibrationRuntimes
      .where('id')
      .equals(calibrationRuntimeId)
      .first();
    if (!cached && updateDcRuntimeDto.woDetailGlobalId) {
      cached = await db.cachedCalibrationRuntimes
        .where('woDetailGlobalId')
        .equals(updateDcRuntimeDto.woDetailGlobalId)
        .first();
    }

    // If the cached runtime has an eventNum, calibration_complete already synced.
    // Skip this save unconditionally — stale saves must never overwrite completed data.
    if (cached?.eventNum) {
      return;
    }

    await dcRuntimeApi.apiDcRuntimeIdPut({
      id: calibrationRuntimeId,
      updateDcRuntimeDto
    });

    const woDetailGlobalId = updateDcRuntimeDto.woDetailGlobalId;
    if (woDetailGlobalId) {
      await db.cachedCalibrationRuntimes
        .where('woDetailGlobalId')
        .equals(woDetailGlobalId)
        .modify({ jsonData: updateDcRuntimeDto.jsonData });

      queryClient.setQueryData(
        [DC_RUNTIME_QUERY_KEY, 'woDetailGlobalId', woDetailGlobalId],
        (old: Record<string, unknown> | undefined) =>
          old
            ? {
                ...old,
                jsonData: updateDcRuntimeDto.jsonData,
                eventNum: updateDcRuntimeDto.eventNum ?? (old as any).eventNum
              }
            : old
      );
    }

    queryClient.setQueryData(
      [DC_RUNTIME_QUERY_KEY, 'id', calibrationRuntimeId],
      (old: Record<string, unknown> | undefined) =>
        old
          ? {
              ...old,
              jsonData: updateDcRuntimeDto.jsonData,
              eventNum: updateDcRuntimeDto.eventNum ?? (old as any).eventNum
            }
          : old
    );
  });

  registerHandler('wo_document_create', async (item: SyncQueueItem) => {
    const { jobNumber, documentData, replaceExisting } = item.payload as {
      jobNumber: string;
      documentData: Record<string, unknown>;
      replaceExisting?: boolean;
    };

    if (replaceExisting) {
      try {
        await workOrdersApi.apiWorkOrdersJobNumberDocumentsDocumentCompanyDocumentNameDelete(
          {
            jobNumber,
            documentCompany: documentData.documentCompany as string,
            documentName: documentData.documentName as string
          }
        );
      } catch {
        /* OK if doesn't exist */
      }
    }

    await workOrdersApi.apiWorkOrdersJobNumberDocumentsPost({
      jobNumber,
      createWorkOrderDocumentCommand:
        documentData as unknown as CreateWorkOrderDocumentCommand
    });

    queryClient.invalidateQueries({
      queryKey: ['workorders', 'documents', jobNumber]
    });
  });

  registerHandler('wo_detail_complete', async (item: SyncQueueItem) => {
    const { jobNumber, sequenceNumber, command } = item.payload as {
      jobNumber: string;
      sequenceNumber: number;
      command?: { eventNumber?: number | null };
    };

    await workOrdersApi.apiWorkOrdersJobNumberDetailsSequenceNumberCompletePost(
      {
        jobNumber,
        sequenceNumber,
        completeWorkOrderDetailCommand: command
      }
    );

    queryClient.invalidateQueries({
      queryKey: workOrderKeys.byJobNumber(jobNumber)
    });
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.details(jobNumber)
    });
  });

  registerHandler('wo_detail_skip', async (item: SyncQueueItem) => {
    const { jobNumber, sequenceNumber, command } = item.payload as {
      jobNumber: string;
      sequenceNumber: number;
      command?: { comments?: string | null };
    };

    await workOrdersApi.apiWorkOrdersJobNumberDetailsSequenceNumberSkipPost({
      jobNumber,
      sequenceNumber,
      skipWorkOrderDetailCommand: command
    });

    queryClient.invalidateQueries({
      queryKey: workOrderKeys.byJobNumber(jobNumber)
    });
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.details(jobNumber)
    });
  });

  registerHandler('wo_detail_incomplete', async (item: SyncQueueItem) => {
    const { jobNumber, sequenceNumber } = item.payload as {
      jobNumber: string;
      sequenceNumber: number;
    };

    await workOrdersApi.apiWorkOrdersJobNumberDetailsSequenceNumberIncompletePost(
      {
        jobNumber,
        sequenceNumber
      }
    );

    queryClient.invalidateQueries({
      queryKey: workOrderKeys.byJobNumber(jobNumber)
    });
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.details(jobNumber)
    });
  });

  registerHandler('wo_complete', async (item: SyncQueueItem) => {
    const { jobNumber, subStatus } = item.payload as {
      jobNumber: string;
      subStatus?: string;
    };

    await bffWorkOrdersApi.apiWorkOrdersJobNumberCompletePost({
      jobNumber,
      completeWorkOrderCommand: { subStatus: subStatus || undefined }
    });

    queryClient.invalidateQueries({
      queryKey: workOrderKeys.byJobNumber(jobNumber)
    });
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.all()
    });
  });

  registerHandler('wo_incomplete', async (item: SyncQueueItem) => {
    const { jobNumber, subStatus } = item.payload as {
      jobNumber: string;
      subStatus?: string;
    };

    await bffWorkOrdersApi.apiWorkOrdersJobNumberIncompletePost({
      jobNumber,
      incompleteWorkOrderCommand: { subStatus: subStatus || undefined }
    });

    queryClient.invalidateQueries({
      queryKey: workOrderKeys.byJobNumber(jobNumber)
    });
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.all()
    });
  });

  registerHandler('charge_add', async (item: SyncQueueItem) => {
    const { jobNumber, chargeData } = item.payload as {
      jobNumber: string;
      chargeData: Record<string, unknown>;
    };

    await workOrdersApi.apiWorkOrdersJobNumberChargesPost({
      jobNumber,
      createWorkOrderChargeCommand: chargeData
    });

    await refreshCachedCharges(jobNumber, item._userId);
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.charges(jobNumber)
    });
  });

  registerHandler('charge_delete', async (item: SyncQueueItem) => {
    const { jobNumber, sequenceNumber } = item.payload as {
      jobNumber: string;
      sequenceNumber: number;
    };

    await workOrdersApi.apiWorkOrdersJobNumberChargesSequenceNumberDelete({
      jobNumber,
      sequenceNumber
    });

    await refreshCachedCharges(jobNumber, item._userId);
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.charges(jobNumber)
    });
  });

  registerHandler('calibration_complete', async (item: SyncQueueItem) => {
    let { calibrationRuntimeId, calibrationId, request } = item.payload as {
      calibrationRuntimeId: string;
      calibrationId: string;
      request: Omit<CompleteCalibrationRequestDto, 'photos'>;
    };

    if (calibrationRuntimeId.startsWith('temp-')) {
      const createItem = await db.syncQueue
        .where('_userId')
        .equals(item._userId)
        .and(
          i =>
            i.entityType === 'dc_runtime_create' &&
            (i.payload as Record<string, unknown>).tempId ===
              calibrationRuntimeId
        )
        .first();

      if (createItem) {
        const dto = (createItem.payload as Record<string, unknown>)
          .createDcRuntimeDto as Record<string, unknown> | undefined;
        const woDetailGlobalId = dto?.woDetailGlobalId as string | undefined;
        if (woDetailGlobalId) {
          const cached = await db.cachedCalibrationRuntimes
            .where('woDetailGlobalId')
            .equals(woDetailGlobalId)
            .first();
          if (cached?.id && !cached.id.startsWith('temp-')) {
            calibrationRuntimeId = cached.id;
          }
        }
      }

      if (calibrationRuntimeId.startsWith('temp-')) {
        throw new Error(
          'DC runtime create has not synced yet — will retry later'
        );
      }
    }

    // Load photos from PhotosDatabase at sync time
    const localPhotos = await photosDb.getPhotosForCalibration(calibrationId);

    const photoResults = await Promise.all(
      localPhotos.map(async photo => {
        try {
          if (!photo.blob) return null;
          const reader = new FileReader();
          const base64Content = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1] || '');
            };
            reader.onerror = reject;
            reader.readAsDataURL(photo.blob);
          });
          return {
            fileName: photo.filename,
            mimeType: photo.mimeType,
            base64Content,
            isCoverPhoto: photo.isCoverPhoto,
            isPromotedToWorkOrder: photo.isPromotedToWorkOrder,
            caption: photo.caption
          };
        } catch {
          return null;
        }
      })
    );

    const photos = photoResults.filter(
      (p): p is NonNullable<typeof p> => p !== null
    );

    const response = await dcRuntimeApi.apiDcRuntimeIdCompletePost({
      id: calibrationRuntimeId,
      completeCalibrationRequestDto: { ...request, photos }
    });

    const responseData = response.data;
    if (
      responseData?.eventNumber &&
      responseData?.syncDetails?.certificateCreated
    ) {
      const {
        jobNumber: payloadJobNumber,
        sequenceNumber: payloadSequenceNumber
      } = item.payload as {
        jobNumber?: string;
        sequenceNumber?: string;
      };
      await db.pendingCertGenerations.add({
        eventNum: responseData.eventNumber,
        calibrationId,
        jobNumber: payloadJobNumber || '',
        sequenceNumber: payloadSequenceNumber || '',
        _userId: item._userId,
        createdAt: new Date().toISOString()
      });
    }

    // Update cached runtime with eventNum so useEventPhotoDocuments can load photos.
    // Also re-fetch the full runtime from the server so the cached jsonData reflects
    // the server-side status update (status.calibration = 'complete') applied by
    // CalibrationDataJsonHelper.UpdateStatus during the Complete endpoint.
    if (responseData?.eventNumber) {
      const cachedRuntime = await db.cachedCalibrationRuntimes
        .where('id')
        .equals(calibrationRuntimeId)
        .first();

      const { woDetailGlobalId: payloadWoDetailGlobalId } = item.payload as {
        woDetailGlobalId?: string;
      };

      const woDetailGlobalId =
        payloadWoDetailGlobalId || cachedRuntime?.woDetailGlobalId;

      // Fetch the updated runtime from the server to get the jsonData with
      // status.calibration = 'complete' set by the backend Complete endpoint
      let updatedRuntime: Record<string, unknown> | null = null;
      try {
        const runtimeResponse = await dcRuntimeApi.apiDcRuntimeIdGet({
          id: calibrationRuntimeId
        });
        updatedRuntime = runtimeResponse.data as Record<string, unknown>;
      } catch {
        // Fall back to just patching eventNum if re-fetch fails
      }

      // Update IndexedDB with full server data (including updated jsonData)
      if (updatedRuntime && cachedRuntime) {
        await db.cachedCalibrationRuntimes.update(calibrationRuntimeId, {
          eventNum: responseData.eventNumber,
          jsonData:
            (updatedRuntime.jsonData as string) ?? cachedRuntime.jsonData
        });
      } else if (cachedRuntime) {
        await db.cachedCalibrationRuntimes.update(calibrationRuntimeId, {
          eventNum: responseData.eventNumber
        });
      }

      // Seed React Query cache with the full updated runtime
      const cacheData = updatedRuntime ? updatedRuntime : undefined;

      if (cacheData) {
        queryClient.setQueryData(
          [DC_RUNTIME_QUERY_KEY, 'id', calibrationRuntimeId],
          cacheData
        );

        if (woDetailGlobalId) {
          queryClient.setQueryData(
            [DC_RUNTIME_QUERY_KEY, 'woDetailGlobalId', woDetailGlobalId],
            cacheData
          );
        }
      } else {
        // Fallback: just patch eventNum if we couldn't fetch the full runtime
        queryClient.setQueryData(
          [DC_RUNTIME_QUERY_KEY, 'id', calibrationRuntimeId],
          (old: Record<string, unknown> | undefined) =>
            old ? { ...old, eventNum: responseData.eventNumber } : old
        );

        if (woDetailGlobalId) {
          queryClient.setQueryData(
            [DC_RUNTIME_QUERY_KEY, 'woDetailGlobalId', woDetailGlobalId],
            (old: Record<string, unknown> | undefined) =>
              old ? { ...old, eventNum: responseData.eventNumber } : old
          );
        }
      }
    }

    // Cache photos as event documents for offline viewing before deleting local copies
    if (responseData?.eventNumber && photos.length > 0) {
      const now = new Date().toISOString();
      const eventDocs: EventDocumentViewModel[] = photos.map(
        (photo, index) => ({
          eventNumber: responseData.eventNumber!,
          documentNumber: `PHOTO-${String(index + 1).padStart(3, '0')}`,
          documentDate: now,
          fileName: photo.fileName,
          description: photo.caption || null,
          comment: null,
          documentType: 'PHOTO',
          fileContent: photo.base64Content,
          printingComplete: null,
          templateType: null,
          templateName: null,
          hideForReadOnly: null,
          revisionLevel: null,
          revisionDate: null,
          documentId: null
        })
      );

      for (const eventDoc of eventDocs) {
        try {
          await db.cachedEventDocuments.add({
            ...eventDoc,
            cachedAt: now,
            _userId: item._userId
          });
        } catch {
          await db.cachedEventDocuments.put({
            ...eventDoc,
            cachedAt: now,
            _userId: item._userId
          });
        }
      }

      queryClient.setQueryData(
        [EVENT_DOCUMENTS_QUERY_KEY, 'eventNumber', responseData.eventNumber],
        eventDocs
      );
    }

    // Only clean up local photos if the server confirmed it processed them
    if (responseData?.eventNumber) {
      await photosDb.deletePhotosForCalibration(calibrationId);
    }

    // Invalidate queries so UI refreshes with server data
    const { jobNumber } = item.payload as { jobNumber?: string };
    if (jobNumber) {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.details(jobNumber)
      });
    }
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.all()
    });
    queryClient.invalidateQueries({
      queryKey: [DC_RUNTIME_QUERY_KEY]
    });
  });

  // --- Rollback handlers for permanent failures ---

  registerRollbackHandler('wo_detail_complete', async (item: SyncQueueItem) => {
    const { jobNumber, sequenceNumber, originalState } = item.payload as {
      jobNumber: string;
      sequenceNumber: number;
      originalState?: string;
    };
    if (!originalState) return;
    await db.cachedWorkOrderDetails
      .where('[jobNumber+sequenceNumber]')
      .equals([jobNumber, sequenceNumber])
      .modify({
        status: originalState as OrderDetailStatus,
        completedDate: null
      });
  });

  registerRollbackHandler('wo_detail_skip', async (item: SyncQueueItem) => {
    const { jobNumber, sequenceNumber, originalState } = item.payload as {
      jobNumber: string;
      sequenceNumber: number;
      originalState?: string;
    };
    if (!originalState) return;
    await db.cachedWorkOrderDetails
      .where('[jobNumber+sequenceNumber]')
      .equals([jobNumber, sequenceNumber])
      .modify({
        status: originalState as OrderDetailStatus
      });
  });

  registerRollbackHandler(
    'wo_detail_incomplete',
    async (item: SyncQueueItem) => {
      const { jobNumber, sequenceNumber, originalState } = item.payload as {
        jobNumber: string;
        sequenceNumber: number;
        originalState?: string;
      };
      if (!originalState) return;
      await db.cachedWorkOrderDetails
        .where('[jobNumber+sequenceNumber]')
        .equals([jobNumber, sequenceNumber])
        .modify({
          status: originalState as OrderDetailStatus
        });
    }
  );

  registerRollbackHandler('wo_complete', async (item: SyncQueueItem) => {
    const { jobNumber, originalState } = item.payload as {
      jobNumber: string;
      originalState?: string;
    };
    if (!originalState) return;
    const cached = await db.cachedWorkOrders
      .where('jobNumber')
      .equals(jobNumber)
      .first();
    if (cached) {
      await db.cachedWorkOrders.update(jobNumber, {
        status: originalState as never
      });
    }
  });

  registerRollbackHandler('wo_incomplete', async (item: SyncQueueItem) => {
    const { jobNumber, originalState } = item.payload as {
      jobNumber: string;
      originalState?: string;
    };
    if (!originalState) return;
    const cached = await db.cachedWorkOrders
      .where('jobNumber')
      .equals(jobNumber)
      .first();
    if (cached) {
      await db.cachedWorkOrders.update(jobNumber, {
        status: originalState as never
      });
    }
  });
}
